import { useTranslation } from "react-i18next";
import { setCustomer } from "@/store/cart.slice";
import { RootState } from "@/store/store";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import i18n from "@/i18n";

type CashierCustomerInputProps = {
  isValidPhoneNumber: boolean;
};

function CashierCustomerInput({
  isValidPhoneNumber,
}: CashierCustomerInputProps) {
  const { t } = useTranslation();
  const customerData = useSelector((state: RootState) => state.cart.customer);
  const dispatch = useDispatch();
  const [isNumberBlured, setIsNumberBlured] = useState(true);
  const [isNameDisabled, setIsNameDisabled] = useState(true);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const BASE_URL = import.meta.env.VITE_BASE_URL;

  useEffect(() => {
    if (!isValidPhoneNumber) {
      dispatch(
        setCustomer({
          name: "",
          phone: customerData.phone,
          _id:"",
        }),
      );
    }
  }, [customerData.phone, dispatch, isValidPhoneNumber]);

  const handlePhoneNumberPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text/plain');
    // Remove all non-digit characters first
    let cleanedNumber = pastedText.replace(/\D/g, '');
    
    // Remove +966 or 966 prefix if present
    if (cleanedNumber.startsWith('966')) {
      cleanedNumber = cleanedNumber.substring(3);
    }
    
    cleanedNumber = cleanedNumber.slice(0, 9);
    
    const input = e.target as HTMLInputElement;
    input.value = cleanedNumber;
    
    dispatch(
      setCustomer({
        name: customerData.name,
        phone: cleanedNumber,
        _id: customerData._id || ""
      })
    );
    
    e.preventDefault();
  };

  const handleCheckUserExist = async () => {
    if (!customerData.phone)
      return toast.error(t("cashierPaymentModal.enterPhone") as string);

    if (!isValidPhoneNumber) {
      return dispatch(
        setCustomer({
          name: "",
          phone: customerData.phone,
          _id: "",
        }),
      );
    }

    const loadingToast = toast.loading(
      t("cashierPaymentModal.loading") as string,
    );
    const url = `${BASE_URL}/api/dashboard/clients/find?phone=${customerData.phone}`;

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: token,
        },
      });

      if (!response.data.success) {
        toast.error(
          response.data.error ||
          t("cashierPaymentModal.failed"),
        );
        return;
      }

      if (response?.data?.exists) {
        dispatch(
          setCustomer({
            name: response?.data?.client?.name,
            phone: customerData.phone,
            _id: response?.data?.client?._id || "",
          }),
        );
        setIsNameDisabled(true);
        toast.success(t("cashierPaymentModal.success") as string);
      } else {
        if (!customerData.name ) {
          dispatch(
            setCustomer({
              name: "",
              phone: customerData.phone,
              _id: "",
            }),
          );
        }
        setIsNameDisabled(false);
        toast.error(
          response.data.error ||  t("cashierPaymentModal.notExist"),
        );
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          t("cashierPaymentModal.failed")
        toast.error(message);
      } else {
        console.error(error);
      }
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  return (
    <div className=" flex flex-col border-b gap-3 border-gray-300 px-2 py-3">
      {/* inputs */}
      <div className="flex flex-wrap gap-2 ">
        {/* Customer Number */}
        <div className="flex w-full flex-col gap-1 flex-[1_1_185px] min-w-[185px]">
          <label className="text-gray-500" htmlFor="customerNumber">
            {t("cashierPaymentModal.phoneLabel")}
          </label>

          <div dir="ltr" className="relative w-full">
            <input
              dir={i18n.language === "ar" ? "rtl" : "ltr" as string}
              placeholder={
                t("cashierPaymentModal.phonePlaceholder") as string
              }
              type="tel"
              value={customerData.phone}
              onPaste={handlePhoneNumberPaste}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 9);
                if (customerData.name) {
                  return dispatch(
                    setCustomer({
                      name: "",
                      phone: value,
                      _id: "",
                    }),
                  );
                }
                if (!customerData.name) {
                  setIsNameDisabled(true);
                }
                dispatch(
                  setCustomer({ name: customerData.name, phone: value ,_id:""}),
                );
              }}
              name="customerNumber"
              id="customerNumber"
              onBlur={() => setIsNumberBlured(true)}
              onFocus={() => setIsNumberBlured(false)}
              maxLength={9}
              className={`h-9 w-full rounded border border-gray-300 ${i18n.language=== "en" ? "pe-2 ps-[3.2rem]": "pe-[3.2rem] ps-2"} `}
            />
            <span className="absolute left-0 top-1/2 flex h-9 w-[3rem] -translate-y-1/2 select-none items-center justify-center rounded-s bg-gray-300 text-sm">
              +966
            </span>
          </div>
          {!isValidPhoneNumber &&
              isNumberBlured && <span className="mb-2 mt-1 block text-xs text-red-500">
            {
              t("cashierPaymentModal.phoneInvalid")}
          </span>}
        </div>

        {/* Customer Name */}
        <div className="flex w-full flex-col gap-1 flex-[1_1_185px] min-w-[185px]">
          <label className="text-gray-500" htmlFor="customerName">
          {t("cashierPaymentModal.nameLabel")}
          </label>
          <input
            placeholder={
              t("cashierPaymentModal.namePlaceholder") as string
            }
            type="text"
            name="customerName"
            value={customerData.name}
            onChange={(e) =>
              dispatch(
                setCustomer({
                  name: e.target.value,
                  phone: customerData.phone,
                  _id: "",
                }),
              )
            }
            disabled={isNameDisabled}
            id="customerName"
            className="h-9 w-full rounded border border-gray-300 ps-2 disabled:cursor-not-allowed disabled:bg-gray-300"
          />
        </div>
      </div>

      {/* Apply Button */}
      <button
        type="button"
        onClick={handleCheckUserExist}
        className="rounded-md bg-sidebar/95 px-10 py-1 text-lg text-skyblue disabled:cursor-not-allowed"
      >
        {t("cashierPaymentModal.button")}
      </button>
    </div>
  );
}

export default CashierCustomerInput;
